// document-group-label.ts — CTSK-498 (Stage 1 of CTSK-497).
//
// ONE normaliser for the two free-text document labels, `group_name` and
// `collection_name`. Every writer calls it — PATCH /documents/:id,
// POST /documents/bulk-edit and any future ingest path — so the rules cannot
// drift between entry points. The database stores whatever it returns.
//
// Pipeline (order matters, see notes):
//   1. null / undefined                      -> null
//   2. non-string                            -> NOT_A_STRING
//   3. trim()                                (JS trim: whitespace + line terminators)
//   4. normalize('NFC')                      (é composed === é decomposed)
//   5. empty                                 -> null   ("" clears the label)
//   6. control / bidi / zero-width character -> INVALID_CHARACTERS
//   7. length in CHARACTERS (code points, not bytes, not UTF-16 units) > 120
//                                            -> TOO_LONG
//   8. otherwise the normalised string, ORIGINAL CASING PRESERVED.
//
// Matching is case-insensitive but storage is not: use groupLabelKey() (or
// lower() in SQL, which the partial indexes in migration 0067 are built on)
// whenever two labels are compared. There is no uniqueness constraint; "Q3
// Audit" and "q3 audit" are the same group, and the most recently written
// casing is what the autocomplete shows.
//
// Rejected characters: C0 controls (U+0000-001F), DEL + C1 controls
// (U+007F-009F), zero-width and directional marks (U+200B-200F), bidi
// embeddings/overrides (U+202A-202E) — the set the spec names — plus, as a
// tightening of the same attack class, the word joiner and invisible math
// operators (U+2060-2064), the bidi isolates (U+2066-2069) and the BOM /
// zero-width no-break space (U+FEFF). Leading/trailing tab and newline are
// removed by trim() before this check, so they are not an error; a tab or
// newline in the MIDDLE is.
import { z } from 'zod';
export const GROUP_LABEL_MAX_LENGTH = 120;
// Exported so other label sanitizers (CTSK-501 normalizeSourceFolder) reuse the
// exact same set instead of keeping a second list that could drift.
// eslint-disable-next-line no-control-regex
export const FORBIDDEN_LABEL_CHARS = /[\u0000-\u001F\u007F-\u009F​-‏‪-‮⁠-⁤⁦-⁩﻿]/;
/** Length in characters (Unicode code points) — what varchar(120) counts. */
export function groupLabelLength(s) {
    return Array.from(s).length;
}
export function normalizeGroupLabel(input) {
    if (input === null || input === undefined)
        return { ok: true, value: null };
    if (typeof input !== 'string') {
        return { ok: false, code: 'NOT_A_STRING', message: 'Label must be a string or null.' };
    }
    const s = input.trim().normalize('NFC');
    if (s === '')
        return { ok: true, value: null };
    if (FORBIDDEN_LABEL_CHARS.test(s)) {
        return {
            ok: false,
            code: 'INVALID_CHARACTERS',
            message: 'Label must not contain control, zero-width or bidirectional-formatting characters.',
        };
    }
    if (groupLabelLength(s) > GROUP_LABEL_MAX_LENGTH) {
        return {
            ok: false,
            code: 'TOO_LONG',
            message: `Label must be at most ${GROUP_LABEL_MAX_LENGTH} characters.`,
        };
    }
    return { ok: true, value: s };
}
/**
 * Case-insensitive comparison key for an already-normalised label (NFC +
 * lower-case). `null` stays `null`. Two labels belong to the same group iff
 * their keys are equal.
 */
export function groupLabelKey(label) {
    if (label === null || label === undefined)
        return null;
    return label.normalize('NFC').toLowerCase();
}
export function groupLabelsEqual(a, b) {
    return groupLabelKey(a) === groupLabelKey(b);
}
/**
 * Zod form of normalizeGroupLabel for body fields: accepts a string or null,
 * emits the normalised `string | null`. Use `.optional()` at the call site so
 * an ABSENT key stays `undefined` ("leave unchanged") while `null` / "" /
 * whitespace-only mean "clear".
 */
export const GroupLabelSchema = z.union([z.string(), z.null()]).transform((input, ctx) => {
    const r = normalizeGroupLabel(input);
    if (!r.ok) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: r.message, params: { labelCode: r.code } });
        return z.NEVER;
    }
    return r.value;
});
//# sourceMappingURL=document-group-label.js.map